import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { api, setAuthTokens, clearAuthTokens, getAccessToken } from "./api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios globally to send cookies
axios.defaults.withCredentials = true;

const AuthCtx = createContext({
  user: null,
  loading: true,
  permissions: null,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
  reloadPermissions: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);

  const reloadPermissions = async () => {
    try {
      const { data } = await api.get(`/roles/me`);
      setPermissions(data);
      return data;
    } catch {
      setPermissions(null);
      return null;
    }
  };

  const refresh = async () => {
    try {
      const { data } = await api.get(`/auth/me`);
      setUser(data);
      // Permissions are fetched alongside the user — keeps nav-gate accurate
      await reloadPermissions();
      return data;
    } catch {
      setUser(null);
      setPermissions(null);
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    // Use the plain `axios` client (not `api`) so a stale Bearer token from
    // a previous session can't poison the login request. The response
    // carries fresh tokens which we persist into localStorage so the
    // Bearer-header interceptor uses them on subsequent calls — this is
    // what makes auth survive 3rd-party-cookie blocking (iframe/Safari).
    const { data } = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    setAuthTokens({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });
    setUser(data.user);
    await reloadPermissions();
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post(`/auth/logout`, {});
    } catch {
      // ignore
    }
    clearAuthTokens();
    setUser(null);
    setPermissions(null);
  };

  return (
    <AuthCtx.Provider
      value={{ user, loading, permissions, login, logout, refresh, reloadPermissions }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}

export const ROLES = {
  SUPERADMIN: "superadmin",
  CONTENT_WRITER: "content_writer",
  MARKETING: "marketing",
};

// Capability helpers — single source of truth for RBAC in the UI
export function can(user, capability) {
  if (!user) return false;
  const role = user.role;
  switch (capability) {
    case "manage_users":
    case "edit_brand_guardrails":
    case "edit_brand_facts":
    case "edit_brand_performance":
    case "delete_any_campaign":
      return role === ROLES.SUPERADMIN;
    case "edit_brand_voice":
      return role === ROLES.SUPERADMIN || role === ROLES.CONTENT_WRITER;
    case "view_brand_profile":
    case "generate_copy":
    case "generate_banner":
    case "save_campaign":
    case "export_zip":
    case "fetch_product":
      return true; // any logged-in user
    default:
      return false;
  }
}

// Route + action gating sourced from the backend /roles/me payload
export function canAccessRoute(permissions, route) {
  if (!permissions) return true; // optimistic until /roles/me loads
  return (permissions.allowed_routes || []).includes(route);
}

export function canPerformAction(permissions, action) {
  if (!permissions) return true;
  return (permissions.allowed_actions || []).includes(action);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    // Proxy layers (Cloudflare etc.) can inject raw error-page text —
    // never show that to the user verbatim.
    if (/cloudflare|origin web server|bad gateway|gateway time-?out|<\s*html/i.test(detail))
      return "The server took too long to respond. Please try again in a moment.";
    return detail;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
